import { AgChartOptions } from "ag-charts-community";
import { AgCharts } from "ag-charts-react";
import {
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Title,
  Loader,
  Center,
  Grid,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconChartBar, IconChartLine, IconBuilding } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { repositorySign } from "../_base/_const/_constVar";
import { MessageResponse } from "../model/MessageResponse";
import {
  StatisticsDataItem,
  StatisticsRequestModel,
  StatisticsResponseModel,
  MonthlyStatisticsData,
  DailyStatisticsData,
} from "../model/StatisticsModel";

interface CompanyStatisticsModalProps {
  opened: boolean;
  onClose: () => void;
}

const CompanyStatisticsModal = ({ opened, onClose }: CompanyStatisticsModalProps) => {
  const [chartType, setChartType] = useState<"bar" | "line" | "pie">("bar");
  const [statisticsType, setStatisticsType] = useState<1 | 2>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [currentData, setCurrentData] = useState<StatisticsDataItem[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  // Set giá trị mặc định khi chuyển sang thống kê theo ngày
  useEffect(() => {
    if (statisticsType === 2 && !fromDate && !toDate) {
      const now = new Date();
      const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      setFromDate(startOfCurrentMonth);
      setToDate(now);
    }
  }, [statisticsType, fromDate, toDate]);


  // Hàm để tải dữ liệu thống kê từ API
  const loadStatisticsData = async () => {
    setIsLoading(true);
    try {
      const requestData: StatisticsRequestModel = {
        Type: statisticsType,
      };

      // Thêm Year cho thống kê theo tháng
      if (statisticsType === 1) {
        requestData.Year = selectedYear;
      } else {
        // Thêm FromDate và ToDate cho thống kê theo ngày
        const now = new Date();
        const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        
        // Sử dụng ngày đã chọn hoặc mặc định từ ngày 1 tháng hiện tại đến ngày hiện tại
        const fromDateToUse = fromDate || startOfCurrentMonth;
        const toDateToUse = toDate || now;
        
        requestData.FromDate = fromDateToUse.toISOString().split('T')[0]; // yyyy-MM-dd
        requestData.ToDate = toDateToUse.toISOString().split('T')[0]; // yyyy-MM-dd
      }

      const response = await repositorySign.post<MessageResponse<StatisticsResponseModel>>(
        "/api/v1/SignPage/contract-sign-statistics-all",
        requestData
      );

      if (response && response.success && response.data) {
        // Chuyển đổi dữ liệu từ API sang format cho biểu đồ
        const chartData: StatisticsDataItem[] = [];
        
        if (statisticsType === 1) {
          // Thống kê theo tháng
          const monthlyData = response.data as unknown as MonthlyStatisticsData[];
          chartData.push(...monthlyData.map(item => ({
            label: item.monthName,
            value: item.signedCount,
            date: `${item.year}-${item.month.toString().padStart(2, '0')}`
          })));
        } else {
          // Thống kê theo ngày
          const dailyData = response.data as unknown as DailyStatisticsData[];
          chartData.push(...dailyData.map(item => ({
            label: `${item.day}/${new Date(item.date).getMonth() + 1}`,
            value: item.signedCount,
            date: item.date.split('T')[0]
          })));
        }
        
        setCurrentData(chartData);
      } else {
        // Nếu API không thành công, hiển thị dữ liệu rỗng
        setCurrentData([]);
      }
    } catch (error) {
      console.error("Error loading company statistics:", error);
      // Nếu có lỗi, hiển thị dữ liệu rỗng
      setCurrentData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Tải dữ liệu khi modal mở hoặc thay đổi loại thống kê
  useEffect(() => {
    if (opened) {
      loadStatisticsData();
    }
  }, [opened, statisticsType, selectedYear, fromDate, toDate]);

  const title = statisticsType === 1 ? "Thống kê toàn công ty theo tháng" : "Thống kê toàn công ty theo ngày";

  const chartOptions: AgChartOptions = chartType === "pie" ? ({
    data: currentData,
    title: {
      text: title,
    },
    series: [
      {
        type: "donut",
        calloutLabelKey: "label",
        angleKey: "value",
        innerRadiusRatio: 0.5,
        fills: [
          "#339af0", "#51cf66", "#ffd43b", "#ff6b6b", "#845ef7", 
          "#fd7e14", "#20c997", "#e83e8c", "#6f42c1", "#fd7e14",
          "#20c997", "#e83e8c"
        ],
        strokes: [
          "#339af0", "#51cf66", "#ffd43b", "#ff6b6b", "#845ef7", 
          "#fd7e14", "#20c997", "#e83e8c", "#6f42c1", "#fd7e14",
          "#20c997", "#e83e8c"
        ],
        label: {
          enabled: true,
          fontSize: 12,
          fontWeight: "bold",
          formatter: (params: any) => {
            return `${params.label}: ${params.value}`;
          },
          position: "inside",
          color: "#fff",
        },
        callout: {
          enabled: false,
        },
        tooltip: {
          renderer: (params: any) => {
            const { datum } = params;
            return {
              content: `${datum.label}: ${datum.value} hợp đồng`,
            };
          },
        },
      },
    ],
    legend: {
      enabled: true,
      position: "right",
    },
    padding: {
      top: 20,
      right: 20,
      bottom: 20,
      left: 20,
    },
  } as unknown as AgChartOptions) : ({
    data: currentData,
    series: [
      {
        type: chartType,
        xKey: "label",
        yKey: "value",
        yName: "Số lượng hợp đồng",
        fill: chartType === "bar" ? "#339af0" : undefined,
        stroke: chartType === "line" ? "#339af0" : undefined,
        strokeWidth: chartType === "line" ? 3 : undefined,
        marker: chartType === "line" ? { fill: "#339af0", stroke: "#339af0" } : undefined,
        label: {
          enabled: true,
          fontSize: 12,
          fontWeight: "bold",
          color: "#333",
          formatter: (params: any) => {
            return params.value.toString();
          },
        },
      },
    ],
    axes: [
      {
        type: "category",
        position: "bottom",
        title: {
          text: statisticsType === 1 ? "Tháng" : "Ngày",
        },
      },
      {
        type: "number",
        position: "left",
        title: {
          text: "Số lượng hợp đồng",
        },
      },
    ],
    legend: {
      enabled: false,
    },
    padding: {
      top: 20,
      right: 20,
      bottom: 20,
      left: 20,
    },
  } as unknown as AgChartOptions);

  const totalContracts = currentData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <IconBuilding size={20} />
          <Title order={4}>Thống kê toàn công ty</Title>
        </Group>
      }
      size="80%"
      centered
    >
      <Stack gap="md">
        {/* Toggle cho loại thống kê */}
        <Group justify="space-between">
          <Text fw={500}>Loại thống kê:</Text>
          <SegmentedControl
            value={statisticsType.toString()}
            onChange={(value) => setStatisticsType(parseInt(value) as 1 | 2)}
            data={[
              { label: "Theo tháng", value: "1" },
              { label: "Theo ngày", value: "2" },
            ]}
          />
        </Group>

        {/* Tùy chọn năm cho thống kê theo tháng */}
        {statisticsType === 1 && (
          <Group justify="space-between">
            <Text fw={500}>Năm:</Text>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value) || new Date().getFullYear())}
              min="2020"
              max="2030"
              style={{
                padding: "8px 12px",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                fontSize: "14px",
                width: "100px"
              }}
            />
          </Group>
        )}

        {/* Tùy chọn ngày cho thống kê theo ngày */}
        {statisticsType === 2 && (
          <Grid>
            <Grid.Col span={6}>
              <DateInput
                valueFormat="DD/MM/YYYY"
                label="Từ ngày"
                placeholder="Chọn từ ngày..."
                value={fromDate}
                onChange={setFromDate}
                clearable
                maxDate={new Date()}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <DateInput
                valueFormat="DD/MM/YYYY"
                label="Đến ngày"
                placeholder="Chọn đến ngày..."
                value={toDate}
                onChange={setToDate}
                clearable
                maxDate={new Date()}
                minDate={fromDate || undefined}
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Toggle cho loại biểu đồ */}
        <Group justify="space-between">
          <Text fw={500}>Loại biểu đồ:</Text>
          <SegmentedControl
            value={chartType}
            onChange={(value) => setChartType(value as "bar" | "line" | "pie")}
            data={[
              { label: "Cột", value: "bar" },
              { label: "Đường", value: "line" },
              { label: "Tròn", value: "pie" },
            ]}
          />
        </Group>

        {/* Thông tin tổng quan */}
        <Group justify="center" p="md" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
          <Stack gap="xs" align="center">
            <Text size="lg" fw={600}>
              Tổng số hợp đồng đã ký: {totalContracts}
            </Text>
            <Text size="sm" c="dimmed">
              Toàn công ty - {statisticsType === 1 ? `Năm ${selectedYear}` : (
                fromDate && toDate 
                  ? `Từ ${fromDate.toLocaleDateString('vi-VN')} đến ${toDate.toLocaleDateString('vi-VN')}`
                  : fromDate 
                    ? `Từ ${fromDate.toLocaleDateString('vi-VN')} đến ngày hiện tại`
                    : toDate
                      ? `Từ đầu tháng đến ${toDate.toLocaleDateString('vi-VN')}`
                      : "Từ đầu tháng đến ngày hiện tại"
              )}
            </Text>
          </Stack>
        </Group>

        {/* Biểu đồ */}
        <div style={{ height: "400px", width: "100%" }}>
          {isLoading ? (
            <Center h="100%">
              <Loader size="lg" />
            </Center>
          ) : (
            <AgCharts options={chartOptions} />
          )}
        </div>

        {/* Nút đóng và tải lại */}
        <Group justify="space-between">
          <Button 
            variant="light" 
            onClick={loadStatisticsData}
            loading={isLoading}
          >
            Tải lại dữ liệu
          </Button>
          <Button variant="outline" onClick={onClose}>
            Đóng
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CompanyStatisticsModal;
